import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Audience } from '@epasu/core';
import {
  CalendarPlus,
  ClipboardList,
  Building2,
  CheckSquare,
  PenSquare,
  Upload,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { crm } from '@/lib/crm-client';
import { ComposeDialog } from '@/components/ComposeDialog';
import { ContactDialog } from '@/components/ContactDialog';
import { EventDialog } from '@/components/EventDialog';
import { OrderDialog } from '@/components/OrderDialog';
import { OrganizationDialog } from '@/components/OrganizationDialog';
import { TaskDialog } from '@/components/TaskDialog';

type Aktion = 'mail' | 'kontakt' | 'firma' | 'aufgabe' | 'auftrag' | 'termin';

const AKTIONEN: { key: Aktion; label: string; icon: LucideIcon }[] = [
  { key: 'mail', label: 'Mail schreiben', icon: PenSquare },
  { key: 'kontakt', label: 'Kontakt', icon: UserPlus },
  { key: 'firma', label: 'Firma', icon: Building2 },
  { key: 'aufgabe', label: 'Aufgabe', icon: CheckSquare },
  { key: 'auftrag', label: 'Auftrag', icon: ClipboardList },
  { key: 'termin', label: 'Termin', icon: CalendarPlus },
];

/**
 * Der Schnellzugriff: was man anlegen will, ohne erst die passende Seite zu
 * suchen.
 *
 * Jeder Knopf öffnet den Dialog, den es ohnehin schon gibt — hier wird nichts
 * nachgebaut, nur früher erreichbar gemacht. „Projekt“ fehlt mit Absicht: ein
 * Projekt gehört immer zu einem Kunden, und die Wahl des Kunden gehört in die
 * Kundenliste, nicht in eine Knopfleiste.
 */
export function QuickActions({ onChanged }: { onChanged: () => void }) {
  const navigate = useNavigate();
  const [offen, setOffen] = useState<Aktion | null>(null);
  const [audiences, setAudiences] = useState<Audience[]>([]);

  // Kontakt und Firma brauchen die Zielgruppen zur Auswahl. Einmal beim
  // Aufbau geholt, damit der Dialog ohne Wartezeit aufgeht.
  useEffect(() => {
    void crm.audiences.list().then(setAudiences).catch(() => setAudiences([]));
  }, []);

  const schliessen = () => setOffen(null);
  const gespeichert = () => {
    setOffen(null);
    onChanged();
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {AKTIONEN.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setOffen(key)}
            className="app-no-drag inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Icon className="h-4 w-4 text-muted-foreground" />
            {label}
          </button>
        ))}
        <button
          onClick={() => navigate('/akquise?reiter=listen&import=1')}
          className="app-no-drag inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <Upload className="h-4 w-4" />
          CSV-Import
        </button>
      </div>

      {offen === 'mail' && <ComposeDialog onClose={schliessen} onSent={gespeichert} />}
      {offen === 'kontakt' && (
        <ContactDialog
          contact={null}
          audiences={audiences}
          onClose={schliessen}
          onSaved={gespeichert}
        />
      )}
      {offen === 'firma' && (
        <OrganizationDialog
          organization={null}
          audiences={audiences}
          onClose={schliessen}
          onSaved={gespeichert}
        />
      )}
      {offen === 'aufgabe' && <TaskDialog onClose={schliessen} onSaved={gespeichert} />}
      {offen === 'auftrag' && (
        <OrderDialog order={null} onClose={schliessen} onSaved={gespeichert} />
      )}
      {offen === 'termin' && <EventDialog onClose={schliessen} onSaved={gespeichert} />}
    </>
  );
}
